import { useState } from 'react';
import type { ReactNode } from 'react';
import type { TLocalize } from '@hooks/use-localize';
import type { ICenterSort, INkrCenterOption, TCenterSortKey } from '@/types/history/nkr-centers';
import Popover from '@components/popover';
import CenterRow from '@views/register/nkr-centers-sections/center-row';

interface IProps {
    centers: INkrCenterOption[];
    t: TLocalize;
    registrationId: number;
    editKey: string;
    isFemale: boolean,
    isLiver: boolean,
    totalTransplantsPopover: string;
    pastYearPopover: string;
    isSubmitting: boolean;
    onSelect: (center: INkrCenterOption) => void;
}

const ADVANCED_REIMBURSEMENT_FALLBACK =
    'Some DCN centers offer advanced reimbursement through Donor Shield, allowing donors to receive reimbursement for eligible expenses before costs are incurred rather than waiting until after the donation process.';

const COLUMN_WIDTHS = [
    '30.0%', // name
    '15.0%', // distance
    '15.0%', // state
    '15.0%', // txps
    '15.0%', // adv reimb
    '10.0%' // button
];

const sortValue = (center: INkrCenterOption, key: TCenterSortKey): string | number => {
    switch (key) {
        case 'name': return center.name.toLowerCase();
        case 'state': return center.state.toLowerCase();
        case 'distance': return center.distanceFromCenter ?? Number.MAX_SAFE_INTEGER;
        case 'ldProtected': return center.industryLdTransplantsViaNkr;
        case 'ldReimbursed': return center.isAdvanceReimbursementEligible ? 1 : 0;
    }
};

const DEFAULT_DESCENDING: TCenterSortKey[] = [
    'ldProtected',
    'ldReimbursed'
];

const CenterTable = ({
    centers,
    t,
    registrationId,
    editKey,
    isFemale,
    isLiver,
    pastYearPopover,
    isSubmitting,
    onSelect,
}: IProps) => {
    const [sort, setSort] = useState<ICenterSort | null>(null);

    const toggleSort = (key: TCenterSortKey) => {
        setSort((current) =>
            current?.key === key ?
                { key, isDescending: !current.isDescending }
                :
                { key, isDescending: DEFAULT_DESCENDING.includes(key) },
        );
    };

    const rows = sort
        ? [...centers].sort((a, b) => {
            const left = sortValue(a, sort.key);
            const right = sortValue(b, sort.key);
            const order = left < right ? -1 : left > right ? 1 : 0;

            return sort.isDescending ? -order : order;
        })
        : centers;

    const renderSortHeader = (key: TCenterSortKey, label: ReactNode, popover?: string) => {
        const link = (
            <a
                href="#"
                onClick={(event) => {
                    event.preventDefault();
                    toggleSort(key);
                }}
            >
                {popover && <span className="fas fa-info-circle icon-nkr-red"></span>}{' '}
                <HeaderLabel content={label} />{' '}
                <span className="fas fa-sort"></span>
            </a>
        );

        return popover ? <Popover content={popover}>{link}</Popover> : link;
    };

    return (
        <>
            <table className="table table-striped table-nkr custom-borders custom-borders-2 cborder-nooutline center-table">
                <colgroup>
                    {COLUMN_WIDTHS.map((width) => (
                        <col key={width} style={{ width }} />
                    ))}
                </colgroup>
                <thead className='hidden-xs'>
                    <tr>
                        <th style={{ textAlign: 'left', paddingLeft: 25 }}>
                            {renderSortHeader(
                                'name',
                                t('DashLabel_CenterName', 'Center Name')
                            )}
                        </th>
                        <th className="hidden-xs">
                            {renderSortHeader(
                                'distance',
                                t('Word_Distance', 'Distance (miles)')
                            )}
                        </th>
                        <th className="hidden-xs">
                            {renderSortHeader(
                                'state',
                                t('Word_State', 'State')
                            )}
                        </th>
                        <th className="hidden-xs">
                            {renderSortHeader(
                                'ldProtected',
                                t('DashLabel_PastYear', 'NKR Transplants Last 12 Months').replaceAll('NKR Transplants ', 'NKR Transplants<br/>'),
                                pastYearPopover,
                            )}
                        </th>
                        <th className="hidden-xs">
                            {renderSortHeader(
                                'ldReimbursed',
                                t('DashLabel_AdvancedReimbursement', 'Advance Reimbursement').replaceAll(' ', '<br/>'),
                                t('DashPopover_AdvancedReimbursement', ADVANCED_REIMBURSEMENT_FALLBACK),
                            )}
                        </th>
                        <th>&nbsp;</th>
                    </tr>
                </thead>
                <tbody className="centerListBody">
                    {rows.map((center) => (
                        <CenterRow
                            key={center.centerId}
                            center={center}
                            t={t}
                            registrationId={registrationId}
                            editKey={editKey}
                            isFemale={isFemale}
                            isLiver={isLiver}
                            isSubmitting={isSubmitting}
                            onSelect={onSelect}
                        />
                    ))}
                </tbody>
            </table>
        </>
    );
};

// Header labels come from the language table, whose entries may carry markup, so a
// string is injected as HTML. A JSX label renders normally.
const HeaderLabel = ({ content }: { content: ReactNode }) =>
    typeof content === 'string' ? (
        <span dangerouslySetInnerHTML={{ __html: content }} />
    ) : (
        <>{content}</>
    );

export default CenterTable;
